import { z } from "zod";
import { BiddingStrategySchema, CalendarDateSchema } from "../campaign-creation";
import {
  SmartCreationObjectiveSchema,
  SmartCreationProductOrganizationSchema,
  SmartCreationStructureSchema,
} from "./draft";
import {
  SmartCreationAutoTargetExpressionSchema,
  SmartCreationStrategyBidSchema,
  SmartCreationStrategyEntityIdSchema,
  SmartCreationTargetingStrategyKindSchema,
} from "./strategy-targets";

export const SmartCreationDraftProductSchema = z
  .object({
    asin: z.string().regex(/^[A-Z0-9]{10}$/),
    /** The listing key (seller SKU or vendor ASIN); execution drops a vendor's before Amazon (#674). */
    sku: z.string().trim().min(1).max(200),
  })
  .strict();

/** Older browser previews remain readable while their current configuration is refreshed. */
const SmartCreationCampaignDraftSchema = z
  .object({
    itemKey: z.string().min(1),
    name: z.string().trim().min(1),
    desiredState: z.literal("PAUSED"),
    startDate: CalendarDateSchema,
    dailyBudget: z.number().positive(),
    biddingStrategy: BiddingStrategySchema,
    targetingType: z.literal("automatic"),
    adGroups: z
      .array(
        z
          .object({
            name: z.string().trim().min(1),
            defaultBid: z.number().positive(),
            products: z.array(SmartCreationDraftProductSchema).min(1).max(500),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

/** Browser preview recovery shape; never a server Plan/Version execution contract. */
const SmartCreationLegacyDraftContentSchema = z
  .object({
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    objective: SmartCreationObjectiveSchema,
    targetAcos: z.number().min(0.01).max(2),
    structure: z.literal("standard"),
    strategy: z.literal("automatic"),
    campaigns: z.array(SmartCreationCampaignDraftSchema).min(1),
    summary: z
      .object({
        campaignCount: z.number().int().positive(),
        adGroupCount: z.number().int().positive(),
        targetCount: z.number().int().positive(),
        totalDailyBudget: z.number().positive(),
      })
      .strict(),
  })
  .strict();

const SMART_CREATION_STABLE_KEY_HASH = "[a-f0-9]{64}";
// Per-creation capacity is independent of the parent-ASIN selection gate.
export const SMART_CREATION_MAX_CAMPAIGNS = 30 as const;
export const SMART_CREATION_MAX_DRAFT_ITEMS = 10_000 as const;

export const SmartCreationCampaignStableKeySchema = z
  .string()
  .regex(new RegExp(`^sc:v1:campaign:${SMART_CREATION_STABLE_KEY_HASH}$`));
export const SmartCreationAdGroupStableKeySchema = z
  .string()
  .regex(new RegExp(`^sc:v1:ad-group:${SMART_CREATION_STABLE_KEY_HASH}$`));
export const SmartCreationTargetStableKeySchema = z
  .string()
  .regex(new RegExp(`^sc:v1:target:${SMART_CREATION_STABLE_KEY_HASH}$`));

const SmartCreationAutomaticTargetDraftContentV2Schema = z
  .object({
    itemKey: SmartCreationTargetStableKeySchema,
    sourceTargetId: SmartCreationStrategyEntityIdSchema,
    strategyKind: z.literal("automatic"),
    expressionType: SmartCreationAutoTargetExpressionSchema,
    bid: SmartCreationStrategyBidSchema,
    // Missing in historical previews, whose generated candidates were all selected.
    included: z.boolean().optional(),
  })
  .strict();

const SmartCreationKeywordTargetDraftContentV2Schema = z
  .object({
    itemKey: SmartCreationTargetStableKeySchema,
    sourceTargetId: SmartCreationStrategyEntityIdSchema,
    strategyKind: SmartCreationTargetingStrategyKindSchema.exclude(["automatic"]),
    keyword: z.string().trim().min(1).max(80),
    bid: SmartCreationStrategyBidSchema,
  })
  .strict();

const SmartCreationProductTargetDraftContentV2Schema = z
  .object({
    itemKey: SmartCreationTargetStableKeySchema,
    sourceTargetId: SmartCreationStrategyEntityIdSchema,
    strategyKind: SmartCreationTargetingStrategyKindSchema.exclude(["automatic"]),
    asin: z
      .string()
      .trim()
      .regex(/^[A-Z0-9]{10}$/),
    bid: SmartCreationStrategyBidSchema,
    matchType: z.enum(["exact", "expanded"]).optional(),
  })
  .strict();

const SmartCreationCategoryTargetDraftContentV2Schema = z
  .object({
    itemKey: SmartCreationTargetStableKeySchema,
    sourceTargetId: SmartCreationStrategyEntityIdSchema,
    strategyKind: SmartCreationTargetingStrategyKindSchema.exclude(["automatic"]),
    categoryId: z.string().trim().min(1).max(128),
    bid: SmartCreationStrategyBidSchema,
  })
  .strict();

const SmartCreationCategoryBrandTargetDraftContentV2Schema = z
  .object({
    itemKey: SmartCreationTargetStableKeySchema,
    sourceTargetId: SmartCreationStrategyEntityIdSchema,
    strategyKind: z.enum(["branded", "competitor"]),
    categoryId: z.string().trim().regex(/^\d+$/),
    brandId: z.string().trim().min(1).max(128),
    bid: SmartCreationStrategyBidSchema,
  })
  .strict();

const SmartCreationAutomaticTargetingDraftContentV2Schema = z
  .object({
    type: z.literal("automatic"),
    targets: z.array(SmartCreationAutomaticTargetDraftContentV2Schema).min(1).max(4),
  })
  .strict();

function validateAutomaticTargetingExpressions(
  targeting: z.infer<typeof SmartCreationAutomaticTargetingDraftContentV2Schema>,
  ctx: z.RefinementCtx,
): void {
  if (
    new Set(targeting.targets.map((target) => target.expressionType)).size !==
    targeting.targets.length
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Automatic targeting requires each expression type exactly once",
      path: ["targets"],
    });
  }
}

const SmartCreationKeywordTargetingDraftContentV2Schema = z
  .object({
    type: z.literal("keyword"),
    matchType: z.enum(["broad", "phrase", "exact"]),
    targets: z.array(SmartCreationKeywordTargetDraftContentV2Schema).min(1).max(500),
  })
  .strict();

const SmartCreationProductTargetingDraftContentV2Schema = z
  .object({
    type: z.literal("product"),
    targets: z.array(SmartCreationProductTargetDraftContentV2Schema).min(1).max(500),
  })
  .strict();

const SmartCreationCategoryTargetingDraftContentV2Schema = z
  .object({
    type: z.literal("category"),
    targets: z.array(SmartCreationCategoryTargetDraftContentV2Schema).min(1).max(500),
  })
  .strict();

const SmartCreationCategoryBrandTargetingDraftContentV2Schema = z
  .object({
    type: z.literal("category_brand"),
    targets: z.array(SmartCreationCategoryBrandTargetDraftContentV2Schema).min(1).max(500),
  })
  .strict();

const SmartCreationTargetingDraftContentV2BaseSchema = z.discriminatedUnion("type", [
  SmartCreationAutomaticTargetingDraftContentV2Schema,
  SmartCreationKeywordTargetingDraftContentV2Schema,
  SmartCreationProductTargetingDraftContentV2Schema,
  SmartCreationCategoryTargetingDraftContentV2Schema,
  SmartCreationCategoryBrandTargetingDraftContentV2Schema,
]);

export const SmartCreationTargetingDraftContentV2Schema =
  SmartCreationTargetingDraftContentV2BaseSchema.superRefine((targeting, ctx) => {
    if (targeting.type === "automatic") {
      validateAutomaticTargetingExpressions(targeting, ctx);
    }
  });
export type SmartCreationTargetingDraftContentV2 = z.infer<
  typeof SmartCreationTargetingDraftContentV2Schema
>;

export const SmartCreationAdGroupDraftContentV2Schema = z
  .object({
    itemKey: SmartCreationAdGroupStableKeySchema,
    name: z.string().trim().min(1),
    defaultBid: SmartCreationStrategyBidSchema,
    products: z.array(SmartCreationDraftProductSchema).min(1).max(500),
    targeting: SmartCreationTargetingDraftContentV2Schema,
  })
  .strict();
export type SmartCreationAdGroupDraftContentV2 = z.infer<
  typeof SmartCreationAdGroupDraftContentV2Schema
>;

export const SmartCreationCampaignDraftContentV2Schema = z
  .object({
    itemKey: SmartCreationCampaignStableKeySchema,
    name: z.string().trim().min(1),
    desiredState: z.literal("PAUSED"),
    startDate: CalendarDateSchema,
    endDate: CalendarDateSchema.nullable().default(null),
    dailyBudget: z.number().finite().positive(),
    biddingStrategy: BiddingStrategySchema,
    targetingType: z.enum(["automatic", "manual"]),
    adGroups: z.array(SmartCreationAdGroupDraftContentV2Schema).min(1).max(3_000),
  })
  .strict()
  .superRefine((campaign, ctx) => {
    if (campaign.endDate !== null && campaign.endDate < campaign.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Campaign end date must be on or after its start date",
        path: ["endDate"],
      });
    }
    for (const [index, adGroup] of campaign.adGroups.entries()) {
      const expectedTargetingType = adGroup.targeting.type === "automatic" ? "automatic" : "manual";
      if (campaign.targetingType !== expectedTargetingType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `A ${campaign.targetingType} Campaign cannot contain ${adGroup.targeting.type} targeting`,
          path: ["adGroups", index, "targeting", "type"],
        });
      }
    }
  });
export type SmartCreationCampaignDraftContentV2 = z.infer<
  typeof SmartCreationCampaignDraftContentV2Schema
>;

/** Retained unchecked automatic candidates do not receive the selected Plan budget. */
export function isSmartCreationDraftCampaignSelected(
  campaign: SmartCreationCampaignDraftContentV2,
): boolean {
  return campaign.adGroups.some(
    (group) =>
      group.targeting.type !== "automatic" ||
      group.targeting.targets.some((target) => target.included !== false),
  );
}

export const SmartCreationStructureCountsSchema = z
  .object({
    campaignCount: z.number().int().nonnegative(),
    adGroupCount: z.number().int().nonnegative(),
    productAdCount: z.number().int().nonnegative(),
    targetCount: z.number().int().nonnegative(),
  })
  .strict();
export type SmartCreationStructureCounts = z.infer<typeof SmartCreationStructureCountsSchema>;

export const SmartCreationBudgetAnalysisSchema = z
  .object({
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    decimals: z.number().int().min(0).max(4),
    totalDailyBudget: z.number().finite().nonnegative(),
    minimumViableDailyBudget: z.number().finite().nonnegative(),
    allocations: z
      .array(
        z
          .object({
            campaignKey: SmartCreationCampaignStableKeySchema,
            dailyBudget: z.number().finite().nonnegative(),
          })
          .strict(),
      )
      .max(SMART_CREATION_MAX_CAMPAIGNS),
  })
  .strict();
export type SmartCreationBudgetAnalysis = z.infer<typeof SmartCreationBudgetAnalysisSchema>;

export const SmartCreationStructureDiagnosticSchema = z
  .object({
    severity: z.enum(["warning", "error"]),
    code: z.enum([
      "conservative_capability_limits",
      "plan_incomplete",
      "start_date_in_past",
      "campaign_limit_exceeded",
      "budget_below_minimum",
      "budget_above_maximum",
      "budget_precision_invalid",
      "bid_out_of_range",
      "bid_precision_invalid",
      "target_capability_unavailable",
      "bidding_strategy_unavailable",
      "products_per_ad_group_limit_exceeded",
      "targets_per_ad_group_limit_exceeded",
      "draftContent_size_exceeded",
      "precise_multiple_variation_groups",
    ]),
    blocking: z.boolean(),
    message: z.string().trim().min(1).max(1_000),
    path: z
      .array(z.union([z.string(), z.number().int().nonnegative()]))
      .max(20)
      .optional(),
    strategyKind: SmartCreationTargetingStrategyKindSchema.optional(),
    targetId: SmartCreationStrategyEntityIdSchema.optional(),
  })
  .strict()
  .superRefine((diagnostic, ctx) => {
    if (diagnostic.severity === "error" && !diagnostic.blocking) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Structure errors must be blocking",
        path: ["blocking"],
      });
    }
    if (diagnostic.severity === "warning" && diagnostic.blocking) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Structure warnings cannot be blocking",
        path: ["blocking"],
      });
    }
  });
export type SmartCreationStructureDiagnostic = z.infer<
  typeof SmartCreationStructureDiagnosticSchema
>;

export const SmartCreationStructureRepairActionSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("increase_budget"),
      minimumTotalDailyBudget: z.number().finite().positive(),
      currencyCode: z.string().regex(/^[A-Z]{3}$/),
    })
    .strict(),
  z
    .object({
      action: z.literal("decrease_budget"),
      maximumTotalDailyBudget: z.number().finite().positive(),
      currencyCode: z.string().regex(/^[A-Z]{3}$/),
    })
    .strict(),
  z.object({ action: z.literal("switch_structure"), structure: z.literal("binary") }).strict(),
  z
    .object({
      action: z.literal("reduce_strategies"),
      strategyKinds: z
        .array(SmartCreationTargetingStrategyKindSchema.exclude(["automatic"]))
        .min(1)
        .max(4)
        .refine((kinds) => new Set(kinds).size === kinds.length, {
          message: "Strategy repair candidates must be unique",
        }),
    })
    .strict(),
  z
    .object({
      action: z.literal("reduce_products"),
      maximumVariationGroups: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      action: z.literal("reduce_targets"),
      maximumTargetsPerAdGroup: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      action: z.literal("reduce_product_ads"),
      maximumProductsPerAdGroup: z.number().int().positive(),
    })
    .strict(),
]);
export type SmartCreationStructureRepairAction = z.infer<
  typeof SmartCreationStructureRepairActionSchema
>;

const SmartCreationDraftContentV2SummarySchema = SmartCreationStructureCountsSchema.extend({
  totalDailyBudget: z.number().finite().positive(),
  minimumViableDailyBudget: z.number().finite().positive(),
})
  .strict()
  .superRefine((summary, ctx) => {
    if (summary.campaignCount < 1 || summary.campaignCount > SMART_CREATION_MAX_CAMPAIGNS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `A generated draft requires between 1 and ${SMART_CREATION_MAX_CAMPAIGNS} Campaigns`,
        path: ["campaignCount"],
      });
    }
    for (const key of ["adGroupCount", "productAdCount", "targetCount"] as const) {
      if (summary[key] < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${key} must be positive for a generated DraftContent`,
          path: [key],
        });
      }
    }
    if (summary.totalDailyBudget < summary.minimumViableDailyBudget) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Generated total budget must satisfy the minimum viable budget",
        path: ["totalDailyBudget"],
      });
    }
  });

export const SmartCreationDraftContentV2Schema = z
  .object({
    schemaVersion: z.literal(2),
    previewGeneratorVersion: z.union([
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
    ]),
    identityVersion: z.literal(1),
    capabilityFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    objective: SmartCreationObjectiveSchema,
    targetAcos: z.number().finite().min(0.01).max(2),
    structure: SmartCreationStructureSchema,
    productOrganization: SmartCreationProductOrganizationSchema,
    campaigns: z
      .array(SmartCreationCampaignDraftContentV2Schema)
      .min(1)
      .max(SMART_CREATION_MAX_CAMPAIGNS),
    summary: SmartCreationDraftContentV2SummarySchema,
  })
  .strict()
  .superRefine((draftContent, ctx) => {
    for (const [campaignIndex, campaign] of draftContent.campaigns.entries()) {
      if (draftContent.structure === "precise" && campaign.adGroups.length !== 1)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Precise requires one Ad Group per Campaign",
          path: ["campaigns", campaignIndex, "adGroups"],
        });
      for (const [groupIndex, group] of campaign.adGroups.entries()) {
        const expected =
          draftContent.structure === "precise"
            ? 1
            : group.targeting.type === "automatic"
              ? 4
              : null;
        if (expected !== null && group.targeting.targets.length !== expected)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `This Structure requires ${expected} Targets per Ad Group`,
            path: ["campaigns", campaignIndex, "adGroups", groupIndex, "targeting", "targets"],
          });
      }
    }
    const adGroups = draftContent.campaigns.flatMap((campaign) => campaign.adGroups);
    const targetKeys = adGroups.flatMap((adGroup) =>
      adGroup.targeting.targets.map((target) => target.itemKey),
    );
    const productAdCount = adGroups.reduce((count, adGroup) => count + adGroup.products.length, 0);
    const counts = {
      campaignCount: draftContent.campaigns.length,
      adGroupCount: adGroups.length,
      productAdCount,
      targetCount: targetKeys.length,
    };
    const draftContentItemCount =
      counts.campaignCount + counts.adGroupCount + counts.productAdCount + counts.targetCount;
    if (draftContentItemCount > SMART_CREATION_MAX_DRAFT_ITEMS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `A generated draft cannot exceed ${SMART_CREATION_MAX_DRAFT_ITEMS} Campaign hierarchy items`,
        path: ["campaigns"],
      });
    }
    for (const key of Object.keys(counts) as Array<keyof typeof counts>) {
      if (draftContent.summary[key] !== counts[key]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${key} must match the generated draft hierarchy`,
          path: ["summary", key],
        });
      }
    }

    const campaignBudgetTotal = draftContent.campaigns.reduce(
      (total, campaign) =>
        total + (isSmartCreationDraftCampaignSelected(campaign) ? campaign.dailyBudget : 0),
      0,
    );
    if (Math.abs(draftContent.summary.totalDailyBudget - campaignBudgetTotal) > 1e-8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "totalDailyBudget must equal the generated Campaign budget total",
        path: ["summary", "totalDailyBudget"],
      });
    }

    const identityGroups = [
      draftContent.campaigns.map((campaign) => campaign.itemKey),
      adGroups.map((adGroup) => adGroup.itemKey),
      targetKeys,
    ];
    for (const [index, identities] of identityGroups.entries()) {
      if (new Set(identities).size !== identities.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Stable DraftContent identities must be unique per object type",
          path: [index === 0 ? "campaigns" : index === 1 ? "adGroups" : "targets"],
        });
      }
    }
  });
export type SmartCreationDraftContentV2 = z.infer<typeof SmartCreationDraftContentV2Schema>;

export const SmartCreationDraftContentSchema = z.union([
  SmartCreationLegacyDraftContentSchema,
  SmartCreationDraftContentV2Schema,
]);
export type SmartCreationDraftContent = z.infer<typeof SmartCreationDraftContentSchema>;
